// GROUND-M M2b VIRTUAL TEXTURE (APP_REARCH §2h): the ground tree's colour —
// tiles generated on the device (groundgen.js, APP_GENSURF §5e: albedo x E /
// W, 512 px over 128 * 2^j m) — in a texture POOL instead of one texture per
// node. A node's level follows its geometric error only; the colour's level
// follows the pixel:
//   - the pools: 2D-array textures of SLOT_PX (512) px RGBA8 sRGB slots with
//     a full mip chain, allocated once (texStorage3D, no CPU copy:
//     source.dataReady false). Pool 0 the naked eye's (renderconfig
//     vt.layers[tier], lowered by by_class[device class]: vtCaps); pool 1 the
//     optic's, allocated at the first focus tile at the size the optic wants
//     (vtGrow), replaced by a larger one while it wants more, up to
//     focus_layers[tier];
//   - a tile in: groundgen.js's GenPass generates it into its slot through
//     THE PRODUCER SEAM below (it renders every mip level of the layer itself
//     — never a whole-array generateMipmap); the tile turns resident (uVtIdx)
//     at its commit;
//   - the indirection uVtIdx: R16UI, slot + 1 per (level, tile), bit 15 =
//     pool 1, the levels packed in rows (uVtLv[j] = first row, nx, ny, tile m);
//   - the fragment (VT_PRE_GLSL, terrain.js V_MORPH): the level whose texel
//     is the pixel's footprint (max(minor, major / anisotropy), from the
//     world-xz derivatives); that level, else the one finer (a dropped
//     parent), else coarser until one is resident; the tap is textureGrad
//     with the derivatives / tile m (groundmat.js GM_MAP_* hooks: the
//     streak tap and the painter's texel read the pool as they read `map`).
// Residency (vtWant, every SCAN_MS; ranks: lower = more wanted):
//   -2 / -1  the top two levels, every tile (the fallback and the boot set);
//   [0, 1)   FLOOR — today's texel at every distance: level j within its ring's
//            radius_m (renderconfig rings), d / radius; a floor tile whose
//            four in-world children are floor and resident is dropped (the
//            fragment takes the finer one);
//   [1, 2)   FILL — toward texel_px pixels (ringtiers.json): level j within
//            R_j = lead * 2 texel_j / (texel_px pa), 1 + d / R_j;
//   focus    the optic's tiles (focus.js levels / range_m / hit — today's focus
//            tile set), d / range, in pool 1: every one the naked pool does
//            not HOLD — its cap best-ranked (the producer's steady state; t40:
//            skipping all it merely wanted left pool 1 empty in the band).
//   pa is the naked eye's 1x pixel angle (groundtree.js: the optic's lives
//   in the focus ranges), so the fill never spends pool 0 on the optic.
// THE PRODUCER SEAM (groundgen.js; every reserve ends in a commit or a release):
//   wanted(now) -> [[key, rank, p, sort score]]  the tiles to generate: wanted, not
//            resident, not reserved, not in a failure's backoff, pool p with
//            layers, in pool 0's HELD SET (vtHeld: its cap best-ranked);
//            near tiles first (_nearLoad: the top two levels and the
//            levels the beam-in latch waits on), then by rank; in play
//            COVERAGE FIRST (vtCover: parents before children, the ones off
//            screen behind; the optic pool: THE VIEW ORDER, vtScore); a FULL pool
//            (filled + reserved + the ones listed before >= its layers) lists
//            only a tile more wanted than its least-wanted resident.
//   reserve(key) -> { key, p, slot, pool, layer, px, levels } | null  its slot:
//            a free one, else the pool's least-wanted resident's (evicted:
//            uVtIdx 0 at once) if less wanted than key; pool p allocated on
//            demand (pool 1 grown when full, its reservations dropped); null:
//            not wanted, resident / reserved already, no pool yet (no
//            renderer, the quiet start) or nothing to evict. The target:
//            pool (the DataArrayTexture), layer = slot, px = SLOT_PX, levels =
//            its mip count. The layer's texel row 0 is the tile's z-max
//            (south) edge: (u, v) = ((x - tx m) / m, 1 - (z - ty m) / m).
//   commit(key) -> bool  the reserved slot written: resident + uVtIdx; false
//            (the slot freed) when the reservation was dropped (_grow1, an
//            eviction) or the tile is no longer wanted.
//   release(key, fail = false)  a reservation given up (its slot frees), or a
//            resident tile evicted (uVtIdx 0); fail: the tile backs off
//            FAIL_MS (nearReady does not wait for it meanwhile).
// A node draws once a level at or coarser than its own is resident under it
// (texReady; latched by the tree). A pool is ALLOCATED in one call (_alloc:
// renderer.initTexture = one texStorage3D) and bound at once — pool 0 from
// the constructor / the first update, behind the Map; pool 1 at its first
// focus tile, small (_grow1). No tile ever waits for the whole pool: a slot
// is usable as soon as its tile is committed. The browser initialises a
// storage-only level on its first write (robust resource init: the WHOLE
// level, every layer) — that cost scales with the pool's layers, hence the
// small first optic pool and the by_class caps. Labels: gm.vt.want (the
// scan), gm.vt.alloc (the allocation).
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { sec, act, heavyOk } from './sched.js';
import { deviceClass, classDraw } from './device.js';
import { texAniso } from './texq.js';
import { LOD_M } from '../../groundm/morph.js';

const VT_CFG = RENDER_CFG.vt;
const _vDir = new THREE.Vector3();

// the slot contract (APP_GENSURF §5e): every level's tile is SLOT_PX square
export const SLOT_PX = 512;
const SCAN_MS = 250, LV_MAX = 8, FAIL_MS = 30e3;

// the fragment's level pick and tap (decl: global scope; pre: main scope,
// before the colour tap). gmS: the picked slot + 1 (bit 15: pool 1), 0 none;
// gmT: its tile; gmUv: world (x, -z) / its tile m — the `vMapUv` of the hooks
// (the streak tap's fall line is in (x, -z), as the tile uv was); gmUx / gmUy
// its screen derivatives, taken from the world's (continuous across levels)
export const VT_DECL_GLSL = `
uniform sampler2DArray uVtPool0;
uniform sampler2DArray uVtPool1;
uniform usampler2D uVtIdx;
uniform vec4 uVtLv[${LV_MAX}];
uniform vec3 uVtPick;
uniform vec2 uVtLook;
uint gmS;
vec2 gmT;
vec4 gmVtTap(vec2 uv, vec2 a, vec2 b) {
  if (gmS == 0u) return vec4(0.5, 0.5, 0.5, 1.0);
  vec3 p = vec3(uv.x - gmT.x, 1.0 + uv.y + gmT.y, float((gmS & 0x7fffu) - 1u));
  vec4 c = (gmS & 0x8000u) != 0u ? textureGrad(uVtPool1, p, a, b) : textureGrad(uVtPool0, p, a, b);
  // look.ground (tunables.gensurf, C9 'uniform'): (gain, sat) — groundgen.js sets it live
  c.rgb = mix(vec3(dot(c.rgb, vec3(0.2126, 0.7152, 0.0722))), c.rgb, uVtLook.y) * uVtLook.x;
  return c;
}
`;
export const VT_PRE_GLSL = `
  vec2 gmW = vec2(vWorldPos2.x, -vWorldPos2.z);
  vec2 gmWx = dFdx(gmW), gmWy = dFdy(gmW);
  float gmM = 1.0;
  {
    float gmA = max(max(length(gmWx), length(gmWy)), 1e-6);
    float gmF = max(abs(gmWx.x * gmWy.y - gmWx.y * gmWy.x) / gmA, gmA / uVtPick.y);
    int gmN = int(uVtPick.z);
    int gmJ = clamp(int(floor(log2(max(gmF / uVtPick.x, 1e-6)))), 0, gmN - 1);
    gmS = 0u; gmT = vec2(0.0);
    for (int i = 0; i < ${LV_MAX}; i++) {
      int k = i == 0 ? gmJ : i == 1 ? gmJ - 1 : gmJ + i - 1;
      if (k < 0 || k >= gmN) continue;
      vec4 lv = uVtLv[k];
      vec2 t = clamp(floor(vWorldPos2.xz / lv.w), vec2(0.0), lv.yz - 1.0);
      uint s = texelFetch(uVtIdx, ivec2(int(t.x), int(lv.x + t.y)), 0).r;
      if (s != 0u) { gmS = s; gmT = t; gmM = lv.w; break; }
    }
  }
  vec2 gmUv = gmW / gmM, gmUx = gmWx / gmM, gmUy = gmWy / gmM;
`;
// three's map_fragment, from the pool (a variant without the streak tap)
export const VT_MAP_GLSL = `
  {
    vec4 sampledDiffuseColor = gmVtTap(gmUv, gmUx, gmUy);
    diffuseColor *= sampledDiffuseColor;
  }
`;
// groundmat.js mapGLSL / groundGLSL hooks under the pool (their defaults:
// three's `map` text, byte for byte)
export const VT_MAP = {
  guard: '#if 1',
  uv: 'gmUv',
  dx: 'gmUx',
  dy: 'gmUy',
  tap: (uv) => `gmVtTap(${uv}, gmUx, gmUy)`,
  grad: (uv, a, b) => `gmVtTap(${uv}, ${a}, ${b})`,
  texels: '512.0',
};

// the texture levels: [{ j, nx, ny, row, m, px }] (meta.levels L{j} tile
// grid; every level SLOT_PX px, tile 128 * 2^j m)
export function vtLevels(meta) {
  const lv = [];
  let row = 0;
  for (let j = 0; j < LV_MAX && meta.levels && meta.levels[`L${j}`]; j++) {
    const L = meta.levels[`L${j}`];
    lv.push({ j, nx: L.nx, ny: L.ny, row, m: 128 * 2 ** j, px: SLOT_PX });
    row += L.ny;
  }
  return lv;
}

// THE WANT SET (pure; validate.py groundm_vt_fit is its twin): lv
// (vtLevels), radii[j] (the ring radius of level j, m; the floor), eye
// { x, z }, pa (rad / px), kpx (texel_px), lead, focus (focus.js or null),
// isRes(key) (resident: the floor drop), cap0 (the naked pool's layers: the
// focus pool takes what lies past them). -> { naked: Map, focus: Map }
// (disjoint) key `${j}_${tx}_${ty}` -> rank
export function vtWant(lv, radii, eye, pa, kpx, lead, focus = null, isRes = () => false, cap0 = Infinity) {
  const naked = new Map(), foc = new Map(), top = lv.length - 1;
  const put = (M, j, tx, ty, rank) => {
    const L = lv[j];
    if (tx < 0 || ty < 0 || tx >= L.nx || ty >= L.ny) return;
    const key = `${j}_${tx}_${ty}`, r = M.get(key);
    if (r === undefined || rank < r) M.set(key, rank);
  };
  const rect = (L, tx, ty) => Math.hypot(Math.max(tx * L.m - eye.x, 0, eye.x - (tx + 1) * L.m),
    Math.max(ty * L.m - eye.z, 0, eye.z - (ty + 1) * L.m));
  const disc = (L, R, fn) => {
    const x0 = Math.max(0, Math.floor((eye.x - R) / L.m)), x1 = Math.min(L.nx - 1, Math.floor((eye.x + R) / L.m));
    const y0 = Math.max(0, Math.floor((eye.z - R) / L.m)), y1 = Math.min(L.ny - 1, Math.floor((eye.z + R) / L.m));
    for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) { const d = rect(L, tx, ty); if (d < R) fn(tx, ty, d); }
  };
  for (const L of lv) {
    if (L.j >= top - 1) {
      for (let ty = 0; ty < L.ny; ty++) for (let tx = 0; tx < L.nx; tx++) put(naked, L.j, tx, ty, L.j === top ? -2 : -1);
      continue;
    }
    const Rf = radii[L.j] || 0;
    if (Rf > 0) disc(L, Rf, (tx, ty, d) => put(naked, L.j, tx, ty, d / Rf));
    if (pa > 0) {
      const R = lead * 2 * (L.m / L.px) / (kpx * pa);
      disc(L, R, (tx, ty, d) => put(naked, L.j, tx, ty, 1 + Math.min(d / R, 0.999)));
    }
  }
  // the floor drop: a floor tile whose in-world children are all floor and
  // resident is never the fragment's pick (it takes the finer one)
  for (const [key, r] of naked) {
    if (!(r >= 0 && r < 1)) continue;
    const [j, tx, ty] = key.split('_').map(Number);
    if (j === 0) continue;
    const C = lv[j - 1];
    let all = true;
    for (let dy = 0; dy < 2 && all; dy++) for (let dx = 0; dx < 2 && all; dx++) {
      const cx = 2 * tx + dx, cy = 2 * ty + dy;
      if (cx >= C.nx || cy >= C.ny) continue;
      const ck = `${j - 1}_${cx}_${cy}`, cr = naked.get(ck);
      if (!(cr >= 0 && cr < 1) || !isRes(ck)) all = false;
    }
    if (all) naked.delete(key);
  }
  if (focus && focus.active && focus.levels) {
    // what the naked pool holds: its cap0 best-ranked (all when it fits)
    const held = naked.size > cap0 ? new Set([...naked].sort((a, b) => a[1] - b[1]).slice(0, cap0).map((e) => e[0])) : naked;
    const on = focus.levelsOn || null;
    for (const L of lv) {
      const f = focus.levels[L.j];
      if (!f || !(f.range_m > 0) || (on && !on.has(L.j))) continue;
      const R = f.range_m, half = L.m * 0.707;
      disc(L, R, (tx, ty, d) => {
        const key = `${L.j}_${tx}_${ty}`;
        if (held.has(key)) return;
        if (!focus.hit((tx + 0.5) * L.m, (ty + 0.5) * L.m, half, R)) return;
        put(foc, L.j, tx, ty, d / R);
        naked.delete(key);              // past the naked cap: pool 1's
      });
    }
  }
  return { naked, focus: foc };
}

// THE VIEW ORDER (painter-gather; igpu owner pose: the 67 on-screen slots of 180 finished at 1026 s of
// 1088 — distance order, no view; the naked pool's play order is now COVERAGE FIRST, vtCover below, on the
// same view, hysteresis and on-screen test): in play (viewOn: groundgen, phase play — the map / beam latch order is
// the plain one), the camera's XZ heading + horizontal half-angle (GmTex.view, main.js tick) orders the
// paint (wanted()'s sort key, vtScore): the top level everywhere first (THE FLOOR: texReady reads up to the
// top, so no node is ever untextured), then every on-screen tile in the plain order — the second level ranks
// SECOND_PLAY there, after the near class's on-screen floor + fill (dx2a, igpu: the old top-two-first order
// painted 36 world-wide slots before any on-screen one; L0 under the eye came ~10 min after load) —, then the
// ones behind in it — the ranks,
// the pool's eviction, the latch (_nearLoad) and validate's vtWant twin never see it. The heading in force
// moves only once the camera has stood past VIEW_HYST of it (or the half-angle past VIEW_HALF_EPS) for
// VIEW_DWELL_MS: a VR glance never reorders. A tile is on screen when it meets the wedge of the half-angle
// + VIEW_PAD, or holds the eye
const VIEW_HYST = Math.cos(20 * Math.PI / 180), VIEW_DWELL_MS = 400, VIEW_PAD = 15 * Math.PI / 180, VIEW_HALF_EPS = 5 * Math.PI / 180;
// V (vtView()) <- the camera's unit XZ dir (dx, dz) + half-angle (rad) at now -> true when the view in force moved
export function vtView() { return { on: false, x: 0, z: -1, half: 0, at: -1, gen: 0 }; }
// (hyst / dwell: the gate's red twin turns them off)
export function vtViewStep(V, dx, dz, half, now, hyst = VIEW_HYST, dwell = VIEW_DWELL_MS) {
  if (!V.on) { V.on = true; V.x = dx; V.z = dz; V.half = half; V.at = -1; V.gen++; return true; }
  if (dx * V.x + dz * V.z >= hyst && Math.abs(half - V.half) <= VIEW_HALF_EPS) { V.at = -1; return false; }
  if (V.at < 0) V.at = now;
  if (now - V.at < dwell) return false;
  V.x = dx; V.z = dz; V.half = half; V.at = -1; V.gen++; return true;
}
// does the ray (ox, oz) + t (dx, dz), t >= 0, meet the box [x0, x1] x [z0, z1]
const rayBox = (ox, oz, dx, dz, x0, x1, z0, z1) => {
  let lo = 0, hi = Infinity;
  for (const [o, d, a, b] of [[ox, dx, x0, x1], [oz, dz, z0, z1]]) {
    if (Math.abs(d) < 1e-12) { if (o < a || o > b) return false; continue; }
    let t0 = (a - o) / d, t1 = (b - o) / d;
    if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
    lo = Math.max(lo, t0); hi = Math.min(hi, t1);
    if (lo > hi) return false;
  }
  return true;
};
// the tile `key` on screen from eye { x, z } under view V (no view yet: every tile) — a corner inside the
// wedge, an edge ray through the box, or the eye in it (a convex wedge meets a box exactly so)
export function vtVis(lv, eye, V, key) {
  if (!V.on) return true;
  const h = V.half + VIEW_PAD;
  if (h >= Math.PI) return true;
  const [j, tx, ty] = key.split('_').map(Number), m = lv[j].m;
  const x0 = tx * m, x1 = x0 + m, z0 = ty * m, z1 = z0 + m;
  if (eye.x >= x0 && eye.x <= x1 && eye.z >= z0 && eye.z <= z1) return true;
  const ch = Math.cos(h);
  for (const x of [x0, x1]) for (const z of [z0, z1]) {
    const dx = x - eye.x, dz = z - eye.z, d = Math.hypot(dx, dz);
    if ((dx * V.x + dz * V.z) >= ch * d) return true;
  }
  const s = Math.sin(h);
  return rayBox(eye.x, eye.z, V.x * ch - V.z * s, V.x * s + V.z * ch, x0, x1, z0, z1)
    || rayBox(eye.x, eye.z, V.x * ch + V.z * s, -V.x * s + V.z * ch, x0, x1, z0, z1);
}
// wanted()'s sort key — the plain order: the near class first (_nearLoad: 0, else 10), then the rank (-2 the
// top level, [0, 1) floor, [1, 2) fill, the second level SECOND_PLAY after them); a tile off screen (vis
// false) and below the top level goes behind every on-screen one (+ 20), in the plain order there.
// THE LATCH ORDER (painter-latch; pre: before the latch, viewOn off — lt: a latch tile, GmTex.latchKey): the
// latch's tiles nearest first ([-3, -2)), then the top level (-2: every node draws once it is resident,
// texReady reads up to the top), then the second level (0) and the rest. The plain order put every tile of
// the top TWO levels first: the live world's 8 + 28 slots at ~1 slot / s hidden (igpu) left none of its 39
// latch tiles painted at the 30 s dismiss timeout (a 1 km window world has a handful). The latch tiles
// before the top (beam-order F1, probe 2026-10-24: the 8 top tiles cost the latch 6-7 s) leave a node
// outside them without a texture at the latch: the overlay holds for the top level (topReady, main.js
// THE TOP GATE), so the first view never shows that hole
// (lead: how far a latch tile moves up — the gate's red twin: 1, the top level first again; second: the
// second level's place in play — the gate's red twin: -1, the top two levels first again)
const SECOND_PLAY = 2;
export function vtScore(r, near, vis, pre = false, lt = false, lead = 3, second = SECOND_PLAY) {
  const s = !pre ? (r === -1 ? second : r) : lt ? r - lead : r === -1 ? 0 : r;
  return (near ? 0 : 10) + s + (!vis && s >= 0 ? 20 : 0);
}

// COVERAGE FIRST (ground-paint-coverage; f1 replays, igpu: 37-64 of ~150 missing on-screen tiles drawn >= 4
// levels coarser — the near class's fine keys painted before the coarse tiles over them, so a miss fell back
// to the top level, the dark zone ahead in the forest): the play order (viewOn) is the top level first (THE
// FLOOR), then the on-screen FRONTIER — a key whose parent is resident (or failed, or not held: nothing to wait
// for) — by its projected size, dm = the distance of its centre from the eye or the look-ahead point (the
// nearer; GmTex.view's walk velocity) over its side, so the coarse cover and the near detail go before the far
// detail and a missing key falls back ONE level; then the on-screen keys whose parent is still missing, then
// the ones behind in the same order. The latch order (pre) and the plain one are vtScore's.
// THE ONE-LEVEL TARGET (OWNER_RULES 2026-10-31: every on-screen near tile at most 1 level coarser by
// 30 s; w52 A/B p1/p2/p3: 35/34/37 on-screen tiles 2+ levels coarser at overlay-off, still 20/28/26 at
// +60 s, as the frontier's L0 detail under the eye took the slots): an on-screen key with a gain
// (vtGain: the on-screen misses it brings to <= 1 level) goes after the top level and before every
// other, the most first, then by dm (the look-ahead kept)
// THE STAND TIER (w54 beam flight, desktop-igpu: the L0 tile under the Beam point, rank 0, score 0.57 — on screen,
// frontier, gain 0: one level coarse — waited 55-60 s behind every gain key): an on-screen near key holding the eye
// (stand: vtStand) goes right after the top level, before the gain tier — the ground under the feet, 1-2 slots
// ... THE FLOOR AFTER THE LATCH (near-floor-after-latch; w54 p1 glide, desktop-igpu: 6 latch tiles evicted after the
// latch, off screen behind the eye, sorted behind every on-screen key — vt_loading 0, terrain.nearReady false 30 s):
// a missing latch tile (floor: GmTex.latchKey, on screen or not) goes in the stand tier, after the on-screen stand keys
export function vtCover(r, vis, front, dm, gain = 0, stand = false, floor = false) {
  if (r === -2) return -100 + Math.min(dm, 99) / 100;
  if (vis && stand) return -95 + Math.min(dm, 99) / 100;
  if (floor) return -94 + Math.min(dm, 99) / 100;
  if (vis && gain > 0) return -90 + (5 - Math.min(gain, 5)) * 10 + Math.min(dm, 99) / 10;
  return (vis ? 0 : 200) + (front ? 0 : 100) + Math.min(dm, 99);
}
// miss: the on-screen held naked keys not resident; res: has(key) -> key -> gain, the misses painting it
// brings to <= 1 level coarser: a miss whose parent is not resident (its fallback 2+ levels) counts for
// itself and for its parent (that one painted, it falls back one level)
// ... a key holding the eye: level j <= near, its centre within STAND_DM of its side from the eye (the tile
// under the eye and, near its edge, the neighbour) — pure
export const STAND_DM = 0.75;
export function vtStand(lv, eye, key, near) {
  const [j, tx, ty] = key.split('_').map(Number);
  if (!(j <= near)) return false;
  const m = lv[j].m;
  return Math.hypot((tx + 0.5) * m - eye.x, (ty + 0.5) * m - eye.z) / m < STAND_DM;
}
export function vtGain(lv, miss, res) {
  const G = new Map(), top = lv.length - 1;
  for (const key of miss) {
    const [j, tx, ty] = key.split('_').map(Number);
    if (j >= top) continue;
    const q = `${j + 1}_${tx >> 1}_${ty >> 1}`;
    if (res.has(q)) continue;
    G.set(key, (G.get(key) || 0) + 1);
    G.set(q, (G.get(q) || 0) + 1);
  }
  return G;
}
// a key's parent (the level above; null: the top)
export function vtParent(lv, key) {
  const [j, tx, ty] = key.split('_').map(Number);
  return j + 1 < lv.length ? `${j + 1}_${tx >> 1}_${ty >> 1}` : null;
}
// THE HELD SET (ground-paint-coverage; f1: 353-366 wanted against a 148-layer pool — the queue painted fill keys
// a better-ranked floor key evicts later, and "every on-screen key resident" could never hold): the naked
// want's cap0 best-ranked keys (ties: the map's order) — what the pool holds at once. null: the want fits.
// Ranks and the want stay vtWant's (validate's twin); wanted() lists only these (resident ones count)
export function vtHeld(naked, cap0) {
  if (!(naked.size > cap0)) return null;
  return new Set([...naked].sort((a, b) => a[1] - b[1]).slice(0, Math.max(0, cap0)).map((e) => e[0]));
}
// THE LOOK-AHEAD (ground-paint-coverage): the eye's walk velocity from two want scans (m / s, smoothed by
// VEL_K; a jump past VEL_JUMP_M — a beam — or a gap past VEL_GAP_MS resets it), the point LOOK_S ahead,
// at most LOOK_MAX_M. Pure: S { x, z, at, vx, vz } updated in place -> the look-ahead point { x, z }
export const LOOK_S = 15, LOOK_MAX_M = 300, VEL_K = 0.5, VEL_JUMP_M = 60, VEL_GAP_MS = 2000;
export function vtAhead(S, x, z, now) {
  const dt = now - S.at, dx = x - S.x, dz = z - S.z;
  if (!(dt > 0) || dt > VEL_GAP_MS || Math.hypot(dx, dz) > VEL_JUMP_M) { S.vx = 0; S.vz = 0; } else {
    S.vx += (dx / dt * 1000 - S.vx) * VEL_K;
    S.vz += (dz / dt * 1000 - S.vz) * VEL_K;
  }
  S.x = x; S.z = z; S.at = now;
  let ax = S.vx * LOOK_S, az = S.vz * LOOK_S;
  const L = Math.hypot(ax, az);
  if (L > LOOK_MAX_M) { ax *= LOOK_MAX_M / L; az *= LOOK_MAX_M / L; }
  return { x: x + ax, z: z + az };
}

// THE LATCH'S TILES (painter-latch; pure): a naked-eye FLOOR tile (rank r in [0, 1)) of a level j <= near
// whose rect meets the STAND reach of the eye — the ground tree's R_STAND for a node of that level
// (morph.js Rs: LOD_M.stand + standW (j + 1) × the class's device.js CLASS_DRAW stand — groundtree.js
// standOf, its stand miss) — the tiles under the beam target, not the level's whole floor ring (the live
// world's L2 floor: 39 slots at 1536 m, the latch never met)
export function vtLatch(lv, eye, key, r, near) {
  if (!(r >= 0 && r < 1)) return false;
  const [j, tx, ty] = key.split('_').map(Number);
  if (j > near || !lv[j]) return false;
  const f = classDraw('stand'), m = lv[j].m, R = (LOD_M.stand + LOD_M.standW * (j + 1)) * f;
  return Math.hypot(Math.max(tx * m - eye.x, 0, eye.x - (tx + 1) * m), Math.max(ty * m - eye.z, 0, eye.z - (ty + 1) * m)) < R;
}

// the finest level's cut (GmTex.minLevel: groundgen's ground.finest_level, raised to the tier's
// pre-latch finest until the latch — groundgen finestFor): no key of a level below it is wanted
// (pure, in place; scripts/check-gpupainter.mjs runs it on vtWant's set)
export function vtCut(want, minLevel) {
  if (minLevel > 0) for (const M of [want.naked, want.focus]) for (const key of M.keys()) if (+key.split('_')[0] < minLevel) M.delete(key);
  return want;
}

// the pools' layers [naked, optic cap] for a detail tier and a device class
// (renderconfig vt.layers / focus_layers per tier; by_class may only lower the naked
// pool, scripts/check-adaptres.mjs keeps it >= the smallest tier's)
export function vtCaps(cfg, tier, cls) {
  const t = String(tier), c = ((cfg.by_class || {})[cls]) || {};
  const n = (cfg.layers || {})[t] ?? 148;
  return [Math.min(n, c.layers ?? n), (cfg.focus_layers || {})[t] ?? 0];
}

// the optic pool's layers for `need` wanted focus tiles, holding `have` (0:
// none yet), under `cap` (renderconfig vt.optic_grow { min, step, k, grow }): `have`
// while it fits, else max(min, need x k, have x grow) rounded up to step
export function vtGrow(need, have, cap, g = {}) {
  const min = g.min ?? 24, step = g.step ?? 8, k = g.k ?? 1.25, grow = g.grow ?? 1.5;
  if (have && Math.min(need, cap) <= have) return have;
  return Math.min(cap, Math.ceil(Math.max(min, Math.min(need, cap) * k, have * grow) / step) * step);
}


export class GmTex {
  // tm: the TerrainManager (rings, _nearLevels); meta: the bake's;
  // tier: the detail tier; getRenderer: () => the WebGLRenderer (null until
  // initDetailQuality ran); kpx: ringtiers texel_px; cls: the device class
  constructor(tm, meta, { tier = 1, getRenderer, kpx = 1, aniso = 8, cls = deviceClass() } = {}) {
    this.tm = tm;
    this.getRenderer = getRenderer;
    this.kpx = kpx;
    this.lead = VT_CFG.lead ?? 1.25;
    this.caps = vtCaps(VT_CFG, tier, cls);   // [naked, the optic's cap]
    this.aniso = aniso = texAniso('ground', aniso, cls);   // texq.js: the class's row under TEX_HQ
    this.lv = vtLevels(meta);
    this.top = this.lv.length - 1;
    this.radii = this.lv.map((L) => { const r = tm.rings.find((q) => q.level === L.j); return r ? r.radius_m : 0; });
    const W = Math.max(1, ...this.lv.map((l) => l.nx)), H = Math.max(1, this.lv.reduce((a, l) => a + l.ny, 0));
    this.idx = new THREE.DataTexture(new Uint16Array(W * H), W, H, THREE.RedIntegerFormat, THREE.UnsignedShortType);
    this.idx.internalFormat = 'R16UI';
    this.idx.minFilter = this.idx.magFilter = THREE.NearestFilter;
    this.idx.generateMipmaps = false;
    this.idx.needsUpdate = true;
    this.uniforms = {
      uVtPool0: { value: null },
      uVtPool1: { value: null },
      uVtIdx: { value: this.idx },
      uVtLv: { value: Array.from({ length: LV_MAX }, (_, j) => (this.lv[j]
        ? new THREE.Vector4(this.lv[j].row, this.lv[j].nx, this.lv[j].ny, this.lv[j].m) : new THREE.Vector4(0, 1, 1, 1))) },
      uVtPick: { value: new THREE.Vector3(this.lv.length ? this.lv[0].m / SLOT_PX : 0.25, aniso, this.lv.length) },
      uVtLook: { value: new THREE.Vector2(1, 1) },
    };
    // residency generation: +1 at every uVtIdx write (a tile in or out) and
    // minLevel move — texReady's inputs (groundtree.js _gen: its still cache)
    this.resGen = 0;
    // ground.finest_level (groundgen.js): levels below it are never wanted / waited for
    this._minLevel = 0;
    this.onScan = null;             // groundgen.js: called after every want scan
    this.onTick = null;             // groundgen.js: called every other update (its start-up: a step a frame)
    this.pools = [null, null];
    this._live = [false, false];    // pool p allocated and bound
    // pool 1 has no layers until its first allocation (_alloc: vtGrow)
    this.slots = [new Array(this.caps[0]).fill(null), []];
    this.res = new Map();           // key -> { p, slot }
    this.want = { naked: new Map(), focus: new Map() };
    this.loading = new Set();       // reserved, not yet committed (the load report)
    this._resv = new Map();         // key -> { p, slot } of a reservation
    this.failed = new Map();        // key -> retry time
    this._at = -Infinity;
    this._fgen = null;
    this.viewOn = false;            // THE VIEW ORDER in force (groundgen: phase play)
    this._V = vtView();             // ... the view in force (view())
    this._eye = { x: 0, z: 0 };     // the last want scan's eye (vtVis)
    this._vel = { x: 0, z: 0, at: -Infinity, vx: 0, vz: 0 };   // the walk velocity (vtAhead)
    this._ahead = { x: 0, z: 0 };   // ... the look-ahead point (COVERAGE FIRST)
    this._held = null;              // THE HELD SET of the want (vtHeld; null: all of it)
    this._visM = new Map();         // key -> on screen, for this view + scan (cleared when either moves)
    // loads: reservations; copies: commits; fails: failed releases
    this.stats = { resident: 0, wanted: 0, focus: 0, loads: 0, copies: 0, fails: 0, evicted: 0, bytes: 0, grows: 0 };
    this._alloc(0);
  }

  get minLevel() { return this._minLevel; }
  set minLevel(v) { if (v !== this._minLevel) { this._minLevel = v; this.resGen++; } }

  // the pool slot bytes (RGBA8 + mips)
  get slotBytes() { return Math.round(SLOT_PX * SLOT_PX * 4 * 4 / 3); }

  rank(key) {
    const a = this.want.naked.get(key), b = this.want.focus.get(key);
    return a !== undefined ? a : b !== undefined ? b : Infinity;
  }

  // eye: world { x, z }; pa: rad / px (the naked eye's); focus: focus.js
  update(eye, pa, focus, now = performance.now()) {
    const fg = focus ? `${focus.active ? 1 : 0}_${focus.gen}_${focus.zoomGen}` : null;
    if (now - this._at >= SCAN_MS || fg !== this._fgen) {
      sec('gm.vt.want');   // upd_by / lt_by labels (sched.js sec)
      this._at = now;
      this._fgen = fg;
      this._eye.x = eye.x; this._eye.z = eye.z;
      this._ahead = vtAhead(this._vel, eye.x, eye.z, now);
      this._visM.clear();
      this.want = vtWant(this.lv, this.radii, eye, pa, this.kpx, this.lead, focus, (key) => this.res.has(key), this.caps[0]);
      vtCut(this.want, this.minLevel);
      this._held = vtHeld(this.want.naked, this.caps[0]);
      this.stats.wanted = this.want.naked.size;
      this.stats.focus = this.want.focus.size;
      if (this.onScan) this.onScan(now);
    } else if (this.onTick) this.onTick(now);
    if (!this._live[0] && this.caps[0]) { sec('gm.vt.alloc'); this._alloc(0); }
  }

  // a node (k, tx, ty; side 128 * 2^k): a level at or coarser than k is
  // resident under its centre — held inside the pool's grid on every side
  // (the far tree's straddlers reach W / N of the frame and past L<top>:
  // VT_PRE_GLSL clamps the tile the same way; a node wholly past the frame
  // never asks: the atlas colours it, groundtree.js _canDraw)
  texReady(k, tx, ty) {
    const x = (tx + 0.5) * 128 * 2 ** k, z = (ty + 0.5) * 128 * 2 ** k;
    for (let j = Math.max(Math.min(k, this.top), Math.min(this.minLevel, this.top)); j <= this.top; j++) {
      const L = this.lv[j];
      if (this.res.has(`${j}_${Math.max(0, Math.min(L.nx - 1, Math.floor(x / L.m)))}_${Math.max(0, Math.min(L.ny - 1, Math.floor(z / L.m)))}`)) return true;
    }
    return false;
  }

  // the near latch's share (groundtree.js): every FLOOR tile of a level <=
  // `levels` resident (a failed one excepted) — today's rings wait for their
  // near tiles' textures the same way, so beam-in never shows the fallback
  nearReady(levels) {
    if (!this.want.naked.size) return false;
    for (const [key, r] of this.want.naked) {
      if (r < 0 || r >= 1 || this.res.has(key) || this.failed.has(key)) continue;
      if (+key.split('_')[0] <= levels && this.latchKey(key, r)) return false;
    }
    return true;
  }

  // THE TOP GATE (beam-order F1; main.js): every tile of the top level resident (a failed one excepted) —
  // texReady reads up to the top, so with it every node draws textured; the beam-in overlay holds for it
  topReady() {
    if (!this.want.naked.size) return false;
    for (const [key, r] of this.want.naked) if (r === -2 && !this.res.has(key) && !this.failed.has(key)) return false;
    return true;
  }

  // THE VIEW ORDER (vtViewStep): main.js tick, every frame — the XR camera in VR, else the camera; the
  // half-angle the frustum's widest horizontal side (an XR camera's is off-axis)
  view(cam, now = performance.now()) {
    if (!cam) return;
    cam.getWorldDirection(_vDir);
    const h = Math.hypot(_vDir.x, _vDir.z);
    if (h < 1e-3) return;                     // straight up / down: no azimuth, the last one holds
    const e = cam.projectionMatrix.elements;
    if (!(Math.abs(e[0]) > 0)) return;
    if (vtViewStep(this._V, _vDir.x / h, _vDir.z / h, Math.atan((1 + Math.abs(e[8])) / Math.abs(e[0])), now)) this._visM.clear();
  }

  // a wanted key's sort key (vtScore; Infinity: not wanted) — groundgen lists its running job by it
  score(key) {
    const a = this.want.naked.get(key);
    if (a !== undefined) return this._score(key, a, 0);
    const b = this.want.focus.get(key);
    return b !== undefined ? this._score(key, b, 1) : Infinity;
  }
  // (pool 1 is the optic's own view: on screen)
  // (in play the naked pool's: COVERAGE FIRST, vtCover)
  _score(key, r, p) {
    let v = !this.viewOn || p === 1 || !this._V.on || this._visM.get(key);
    if (v === undefined) this._visM.set(key, (v = vtVis(this.lv, this._eye, this._V, key)));
    if (this.viewOn && p === 0) return vtCover(r, v, this._front(key), this._dm(key), v ? this._gain(key) : 0, v && vtStand(this.lv, this._eye, key, this.tm._nearLevels ?? 2), this.latchKey(key, r));
    return vtScore(r, this._nearLoad(key), v, !this.viewOn, !this.viewOn && p === 0 && this.latchKey(key, r));
  }
  // a frontier key: its parent resident, failed, unwanted or not held (nothing to wait for), or none
  _front(key) {
    const q = vtParent(this.lv, key);
    if (!q || this.res.has(q) || this.failed.has(q) || !this.want.naked.has(q)) return true;
    return !!this._held && !this._held.has(q);
  }
  // its vtGain over the on-screen held misses: made once per scan, residency change (resGen) and view
  _gain(key) {
    const g = `${this._at}_${this.resGen}_${this._V.gen}`;
    if (this._gainG !== g) {
      this._gainG = g;
      const miss = [];
      let on = 0, deep = 0;
      for (const k of this.want.naked.keys()) {
        if (this._held && !this._held.has(k)) continue;
        let v = this._visM.get(k);
        if (v === undefined) this._visM.set(k, (v = vtVis(this.lv, this._eye, this._V, k)));
        if (!v) continue;
        on++;
        if (this.res.has(k)) continue;
        miss.push(k);
        const q = vtParent(this.lv, k);
        if (!q || !this.res.has(q)) deep++;
      }
      this._gainM = vtGain(this.lv, miss, this.res);
      this._coarse = { on, miss: miss.length, deep };
    }
    return this._gainM.get(key) || 0;
  }
  // THE ONE-LEVEL COUNT (a reader: the beam flight paces on it, w54): the on-screen held naked keys at the
  // last scan's eye and the view in force (none yet: all) — { on, miss: not resident, deep: 2+ levels
  // coarser or nothing under it: the parent not resident either (OWNER_RULES 2026-10-31: 0 by 30 s) }; the gain's cache
  coarse() { this._gain(''); return this._coarse; }
  // a key's one-level gain in play (0 before it): groundgen leads with it — gensched picks a pass's best
  // LEADING tile, so a near-class key (L0 detail, gain 0) would otherwise outrank the gain tier's key
  gain(key) { return this.viewOn ? this._gain(key) : 0; }
  // a key the play order puts first — the stand tier (a missing latch tile included) or the gain tier (vtCover):
  // groundgen leads with it and admits it first at the input cap (gap first)
  urgent(key) {
    if (!this.viewOn) return false;
    if (this._gain(key) > 0) return true;
    if (!this.want.naked.has(key)) return false;
    if (this.latchKey(key)) return true;
    let v = !this._V.on || this._visM.get(key);
    if (v === undefined) this._visM.set(key, (v = vtVis(this.lv, this._eye, this._V, key)));
    return !!v && vtStand(this.lv, this._eye, key, this.tm._nearLevels ?? 2);
  }
  // its centre's distance from the eye or the look-ahead point (the nearer) over its side
  _dm(key) {
    const [j, tx, ty] = key.split('_').map(Number), m = this.lv[j].m, x = (tx + 0.5) * m, z = (ty + 0.5) * m;
    return Math.min(Math.hypot(x - this._eye.x, z - this._eye.z), Math.hypot(x - this._ahead.x, z - this._ahead.z)) / m;
  }

  // a tile the beam-in latch waits on (nearReady; groundgen's beam list): vtLatch at the last scan's eye
  latchKey(key, r = this.want.naked.get(key)) {
    return vtLatch(this.lv, this._eye, key, r, this.tm._nearLevels ?? 2);
  }

  // THE PRODUCER SEAM (header): the tiles to generate, near first, then by rank (in play: off screen behind, vtScore)
  wanted(now = performance.now()) {
    const cand = [];
    for (const [M, p] of [[this.want.naked, 0], [this.want.focus, 1]]) {
      if (!this.caps[p]) continue;
      // (the naked pool: THE HELD SET only — a key past it would be evicted by a better-ranked one)
      const H = p === 0 ? this._held : null;
      for (const [key, r] of M) if (!this.res.has(key) && !this._resv.has(key) && !((this.failed.get(key) || 0) > now) && (!H || H.has(key))) cand.push([key, r, p, this._score(key, r, p)]);
    }
    // the sort key made once a candidate (c[3]), not in the comparator (a key split each compare:
    // groundgen's scan.want, up to 27 ms a scan on quest — gpupainter-p4-3)
    cand.sort((a, b) => a[3] - b[3]);
    // the pools' filled slots (reservations included) and least-wanted rank:
    // once a call, not once a candidate (t37)
    const filled = this.slots.map((S) => S.reduce((a, s) => a + (s ? 1 : 0), 0)), worst = [null, null];
    const out = [];
    for (const c of cand) {
      const p = c[2];
      // full: only a tile more wanted than the pool's least-wanted resident
      if (filled[p] >= this.caps[p] && !(c[1] < (worst[p] ??= this._worst(p).rank))) continue;
      filled[p]++;
      out.push(c);
    }
    return out;
  }

  reserve(key) {
    if (this.res.has(key) || this._resv.has(key) || !this.lv.length) return null;
    const p = this.want.naked.has(key) ? 0 : this.want.focus.has(key) ? 1 : -1;
    if (p < 0) return null;
    // pool 1: allocated at its first tile (one call), replaced by a larger
    // one when full and the optic wants more
    if (p && this._live[1] && this.slots[1].indexOf(null) < 0) this._grow1();
    if (!this._live[p]) { sec('gm.vt.alloc'); if (!this._alloc(p)) return null; }
    const slot = this._slot(p, this.rank(key));
    if (slot < 0) return null;
    this.slots[p][slot] = key;
    this._resv.set(key, { p, slot });
    this.loading.add(key);
    this.stats.loads++;
    return { key, p, slot, pool: this.pools[p], layer: slot, px: SLOT_PX, levels: Math.log2(SLOT_PX) + 1 };
  }

  commit(key) {
    const t = this._resv.get(key);
    if (!t) return false;
    this._resv.delete(key);
    this.loading.delete(key);
    if (this.slots[t.p][t.slot] !== key) return false;
    // no longer wanted: the slot frees (nothing reads it)
    if (this.rank(key) === Infinity) { this.slots[t.p][t.slot] = null; return false; }
    this.res.set(key, { p: t.p, slot: t.slot });
    this._setIdx(key, (t.slot + 1) | (t.p ? 0x8000 : 0));
    this.stats.copies++;
    this.stats.resident = this.res.size;
    return true;
  }

  release(key, fail = false) {
    const t = this._resv.get(key), r = this.res.get(key);
    if (t) {
      this._resv.delete(key);
      this.loading.delete(key);
      if (this.slots[t.p][t.slot] === key) this.slots[t.p][t.slot] = null;
    } else if (r) {
      this.res.delete(key);
      this._setIdx(key, 0);
      if (this.slots[r.p][r.slot] === key) this.slots[r.p][r.slot] = null;
      this.stats.resident = this.res.size;
    }
    if (fail) {
      this.stats.fails++;
      this.failed.set(key, performance.now() + FAIL_MS);
      if (this.stats.fails <= 3) console.warn('groundm vt: generation failed', key);
    }
  }

  // a level's class: 'near' for the top two levels and every level
  // nearReady waits for (<= near_levels), else 'bulk'
  _nearLoad(key) {
    const j = +key.split('_')[0];
    return j >= this.top - 1 || j <= this.tm._nearLevels;
  }

  // pool p's least-wanted resident (a reserved slot is never one)
  _worst(p) {
    let w = { rank: -Infinity, slot: -1 };
    const S = this.slots[p];
    for (let s = 0; s < S.length; s++) {
      if (!S[s] || this._resv.has(S[s])) continue;
      const r = this.rank(S[s]);
      if (r > w.rank) w = { rank: r, slot: s };
    }
    return w;
  }

  // pool p: SLOT_PX^2 x its slots sRGB RGBA8, the full mip chain, storage only
  _pool(p) {
    if (this.pools[p]) return this.pools[p];
    const px = SLOT_PX, n = this.slots[p].length;
    const pool = new THREE.DataArrayTexture(null, px, px, n);
    pool.colorSpace = THREE.SRGBColorSpace;
    pool.minFilter = THREE.LinearMipmapLinearFilter;
    pool.magFilter = THREE.LinearFilter;
    pool.wrapS = pool.wrapT = THREE.ClampToEdgeWrapping;
    pool.anisotropy = this.aniso;
    // texStorage3D with log2(px) + 1 levels and no data, no generateMipmap:
    // the levels are the placeholders' count (three getMipLevels)
    pool.generateMipmaps = false;
    pool.mipmaps = Array.from({ length: Math.log2(px) + 1 }, (_, l) => ({ data: null, width: px >> l, height: px >> l, depth: n }));
    pool.source.dataReady = false;
    pool.needsUpdate = true;
    this.pools[p] = pool;
    this.stats.bytes += this.slotBytes * n;
    return pool;
  }

  // allocate pool p and bind it: ONE call — renderer.initTexture (three:
  // texStorage3D of the whole chain, no data: source.dataReady false; then it
  // unbinds through its own state cache). No renderer yet: false (the first
  // update / reserve for p allocates it). An empty slot is never read (uVtIdx 0).
  _alloc(p) {
    if (this._live[p]) return true;
    const r = this.getRenderer && this.getRenderer();
    // quiet start (sched.js heavyOk): no ~270 MB pool before the first card / the Beam
    if (!r || !heavyOk() || this._disposed || !this.caps[p] || !this.lv.length) return false;
    // the optic's: as many layers as it wants now (vtGrow), not its cap
    if (p && !this.slots[1].length) this.slots[1] = new Array(vtGrow(this.want.focus.size, 0, this.caps[1], VT_CFG.optic_grow)).fill(null);
    const pool = this._pool(p);
    act('gm.vt.alloc', () => r.initTexture(pool));
    this.uniforms[p ? 'uVtPool1' : 'uVtPool0'].value = pool;
    this._live[p] = true;
    return true;
  }

  // pool 1 full while the optic wants more than it holds: a larger pool
  // (vtGrow, up to the cap) replaces it at the next _alloc; its tiles leave
  // uVtIdx (the fragment takes the coarser naked levels) and regenerate; its
  // reservations drop (their commit is false). false: it cannot grow.
  _grow1() {
    const have = this.slots[1].length, n = vtGrow(this.want.focus.size, have, this.caps[1], VT_CFG.optic_grow);
    if (n <= have) return false;
    for (const key of this.slots[1]) {
      if (!key) continue;
      if (this._resv.has(key)) { this._resv.delete(key); this.loading.delete(key); } else { this.res.delete(key); this._setIdx(key, 0); }
    }
    this.pools[1].dispose();
    this.pools[1] = null;
    this._live[1] = false;
    this.uniforms.uVtPool1.value = null;
    this.stats.bytes -= this.slotBytes * have;
    this.slots[1] = new Array(n).fill(null);
    this.stats.resident = this.res.size;
    this.stats.grows++;
    return true;
  }

  // a free slot of pool p, else its least-wanted one (if less wanted than rank)
  _slot(p, rank) {
    const S = this.slots[p], free = S.indexOf(null);
    if (free >= 0) return free;
    const w = this._worst(p);
    if (w.slot < 0 || !(w.rank > rank)) return -1;
    this.res.delete(S[w.slot]);
    this._setIdx(S[w.slot], 0);
    S[w.slot] = null;
    this.stats.evicted++;
    this.stats.resident = this.res.size;
    return w.slot;
  }

  // uVtIdx: needsUpdate bumps the version — three uploads it once, at the
  // frame's next draw (an eviction's 0 goes up before the frame draws over
  // the slot's new levels)
  _setIdx(key, v) {
    const [j, tx, ty] = key.split('_').map(Number), L = this.lv[j];
    this.idx.image.data[(L.row + ty) * this.idx.image.width + tx] = v;
    this.idx.needsUpdate = true;
    this.resGen++;
  }

  // the finest resident level's texel (m) at world (x, z) (load report)
  texelAt(x, z) {
    for (const L of this.lv) if (this.res.has(`${L.j}_${Math.floor(x / L.m)}_${Math.floor(z / L.m)}`)) return L.m / L.px;
    return Infinity;
  }

  dispose() {
    this._disposed = true;
    this._resv.clear();
    this.loading.clear();
    for (const p of this.pools) if (p) p.dispose();
    this.idx.dispose();
  }
}
